import React, { useEffect, useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { ArrowLeft, CreditCard, Save, Loader2, CheckCircle2, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { useAdminAuth } from './AdminAuthContext';
import { adminApi } from './adminApi';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { useAdminOtpGuard } from './useAdminOtpGuard';
import { RazorpaySignatureTest, RazorpayDeliveries } from './RazorpayWebhookDiagnostics';

export function AdminPaymentIntegrationsPage() {
  const { admin, loading } = useAdminAuth();
  const [config, setConfig] = useState(null);
  const [keyId, setKeyId] = useState('');
  const [keySecret, setKeySecret] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const [showSecret, setShowSecret] = useState(false);
  const [showWebhookSecret, setShowWebhookSecret] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [standardInr, setStandardInr] = useState('2499');
  const [proInr, setProInr] = useState('4499');
  const [fetchLoading, setFetchLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Dual-channel (Email + WhatsApp) OTP guard (Part D).
  const { guard, dialog: otpDialog } = useAdminOtpGuard();

  const refresh = async () => {
    try {
      const r = await adminApi.get('/admin/payment-integrations');
      setConfig(r.data);
      setKeyId(r.data.razorpay.key_id || '');
      setEnabled(r.data.razorpay.enabled);
      const map = r.data.razorpay.webhook_tier_amounts_inr || {};
      if (map.standard != null) setStandardInr(String(map.standard));
      if (map.pro != null) setProInr(String(map.pro));
    } catch {
      toast.error('Could not load payment integrations');
    } finally {
      setFetchLoading(false);
    }
  };

  useEffect(() => {
    if (admin) refresh();
  }, [admin]);

  if (loading) return null;
  if (!admin) return <Navigate to="/admin/login" replace />;

  const handleSave = async (e) => {
    e.preventDefault();
    if (!keyId.trim()) {
      toast.error('Key ID is required');
      return;
    }
    await guard({
      requestOtpUrl: '/admin/payment-integrations/request-otp',
      title: 'Confirm Razorpay save',
      description: 'Saving Razorpay credentials needs dual Email + WhatsApp OTP verification.',
      performSave: async (headers) => {
        setSaving(true);
        try {
          await adminApi.put('/admin/payment-integrations/razorpay', {
            key_id: keyId.trim(),
            // Empty string = leave existing secret untouched
            key_secret: keySecret.trim() || null,
            webhook_secret: webhookSecret.trim() || null,
            webhook_tier_amounts_inr: {
              standard: parseInt(standardInr, 10) || 2499,
              pro: parseInt(proInr, 10) || 4499,
            },
            enabled,
          }, { headers });
          toast.success('Razorpay configuration saved');
          setKeySecret('');
          setWebhookSecret('');
          await refresh();
        } finally {
          setSaving(false);
        }
      },
    });
  };

  const razor = config?.razorpay;

  return (
    <div
      data-testid="admin-payments-page"
      className="min-h-screen"
      style={{
        background: 'radial-gradient(circle at 0% 0%, #1e293b 0%, #0f172a 50%, #020617 100%)',
      }}
    >
      <header className="border-b border-slate-800 bg-slate-950/60 backdrop-blur-xl">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center gap-3">
          <Link
            to="/admin"
            className="w-9 h-9 rounded-lg bg-slate-900 border border-slate-800 flex items-center justify-center hover:bg-slate-800"
            data-testid="admin-payments-back"
          >
            <ArrowLeft className="w-4 h-4 text-slate-300" />
          </Link>
          <div className="w-9 h-9 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center">
            <CreditCard className="w-4 h-4 text-amber-400" />
          </div>
          <div>
            <div className="text-white font-semibold">Payment Integrations</div>
            <div className="text-xs text-slate-400">
              Manage gateway credentials for in-app upgrades
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8">
        {fetchLoading ? (
          <div className="flex items-center gap-2 text-slate-400">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : (
          <form
            onSubmit={handleSave}
            data-testid="razorpay-form"
            className="bg-slate-900/70 border border-slate-800 rounded-2xl p-6 space-y-5"
          >
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-lg bg-blue-500/10 border border-blue-500/30 flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <div className="text-white font-semibold flex items-center gap-2">
                    Razorpay
                    {razor?.is_secret_configured && (
                      <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">
                        <CheckCircle2 className="inline w-2.5 h-2.5 mr-1" /> Configured
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-400">
                    Used for in-app Pro upgrades (₹{config?.pro_upgrade_inr?.toLocaleString('en-IN')} one-time).
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Label htmlFor="rzp-enabled" className="text-xs text-slate-300 uppercase tracking-wider">
                  Enabled
                </Label>
                <Switch
                  id="rzp-enabled"
                  checked={enabled}
                  onCheckedChange={setEnabled}
                  data-testid="razorpay-enabled-switch"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-slate-300 text-xs uppercase tracking-wider">
                Key ID (public)
              </Label>
              <Input
                value={keyId}
                onChange={(e) => setKeyId(e.target.value)}
                placeholder="rzp_test_xxxxxxxxxxxx or rzp_live_xxxxxxxxxxxx"
                required
                data-testid="razorpay-key-id"
                className="bg-slate-800/50 border-slate-700 text-white font-mono text-sm"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-slate-300 text-xs uppercase tracking-wider">
                Key Secret (private)
              </Label>
              <div className="relative">
                <Input
                  type={showSecret ? 'text' : 'password'}
                  value={keySecret}
                  onChange={(e) => setKeySecret(e.target.value)}
                  placeholder={
                    razor?.is_secret_configured
                      ? '•••••••• (leave blank to keep current secret)'
                      : 'Paste your Razorpay key secret'
                  }
                  data-testid="razorpay-key-secret"
                  className="bg-slate-800/50 border-slate-700 text-white font-mono text-sm pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowSecret((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                  tabIndex={-1}
                >
                  {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-[10px] text-slate-500">
                Stored encrypted in MongoDB. Never displayed back through the API once saved.
              </p>
            </div>

            <div className="rounded-lg bg-blue-500/5 border border-blue-500/20 p-3 text-xs text-blue-200/90">
              <strong>Where to find your keys:</strong> Razorpay Dashboard → Settings → API Keys.
              Use <span className="font-mono">rzp_test_*</span> while building, then switch to{' '}
              <span className="font-mono">rzp_live_*</span> when ready to charge real money.
            </div>

            {/* -------- Razorpay Webhook (native inbound events) -------- */}
            <div className="pt-4 mt-4 border-t border-slate-800 space-y-4">
              <div>
                <div className="text-white font-semibold flex items-center gap-2">
                  Webhook receiver
                  {razor?.is_webhook_secret_configured && (
                    <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">
                      <CheckCircle2 className="inline w-2.5 h-2.5 mr-1" /> Configured
                    </span>
                  )}
                </div>
                <div className="text-xs text-slate-400">
                  Mints licenses automatically when Razorpay fires <code className="text-slate-300">payment.captured</code>.
                </div>
              </div>

              <div className="rounded-lg bg-slate-800/50 border border-slate-700 p-3 space-y-2">
                <div className="text-[10px] uppercase tracking-wider text-slate-500">Point Razorpay at</div>
                <div className="font-mono text-sm text-emerald-300 break-all" data-testid="razorpay-webhook-url">
                  {(config?.public_backend_url || window.location.origin.replace('preview.', 'app.'))}/api/webhooks/razorpay
                </div>
                <div className="text-[11px] text-slate-500">
                  Copy this URL into Razorpay Dashboard → Webhooks → URL. Enable event <code className="text-slate-300">payment.captured</code>.
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="text-slate-300 text-xs uppercase tracking-wider">
                  Webhook secret (from Razorpay Dashboard)
                </Label>
                <div className="relative">
                  <Input
                    type={showWebhookSecret ? 'text' : 'password'}
                    value={webhookSecret}
                    onChange={(e) => setWebhookSecret(e.target.value)}
                    placeholder={
                      razor?.is_webhook_secret_configured
                        ? '•••••••• (leave blank to keep current webhook secret)'
                        : 'Paste the secret shown in Razorpay Webhooks page'
                    }
                    data-testid="razorpay-webhook-secret"
                    className="bg-slate-800/50 border-slate-700 text-white font-mono text-sm pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowWebhookSecret((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                    tabIndex={-1}
                  >
                    {showWebhookSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-[10px] text-slate-500">
                  Distinct from the Key Secret above. Razorpay shows it once when you create the webhook — save it here immediately.
                </p>
                {razor?.webhook_secret_last4 && (
                  <p className="text-[11px] text-slate-400" data-testid="razorpay-webhook-secret-last4">
                    Saved secret ends with <span className="font-mono text-amber-300">…{razor.webhook_secret_last4}</span> — must match the secret in Razorpay → Webhooks.
                  </p>
                )}
              </div>

              <RazorpaySignatureTest configured={razor?.is_webhook_secret_configured} />

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-slate-300 text-xs uppercase tracking-wider">
                    Standard tier price (₹)
                  </Label>
                  <Input
                    type="number"
                    min="1"
                    value={standardInr}
                    onChange={(e) => setStandardInr(e.target.value)}
                    data-testid="razorpay-tier-standard"
                    className="bg-slate-800/50 border-slate-700 text-white font-mono text-sm"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-slate-300 text-xs uppercase tracking-wider">
                    Pro tier price (₹)
                  </Label>
                  <Input
                    type="number"
                    min="1"
                    value={proInr}
                    onChange={(e) => setProInr(e.target.value)}
                    data-testid="razorpay-tier-pro"
                    className="bg-slate-800/50 border-slate-700 text-white font-mono text-sm"
                  />
                </div>
              </div>
              <p className="text-[10px] text-slate-500 -mt-2">
                Incoming <code>payment.captured</code> events with these amounts (in paise) mint the matching tier. If Razorpay's <code>notes.tier</code> is set it wins over amount matching.
              </p>
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <Button
                type="submit"
                disabled={saving}
                data-testid="razorpay-save"
                className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" /> Save Razorpay configuration
                  </>
                )}
              </Button>
            </div>
          </form>
        )}
        {!fetchLoading && <RazorpayDeliveries />}
      </main>
      {otpDialog}
    </div>
  );
}
